import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import {
    useAuthToken,
    useRequestUrl,
    useFullCourtData,
    useCourtsByAgency,
    useAgencyCourt,
    useCurrentUser,
    useLogEntry
} from "../context/contextHooks";
import API from "../http/API";
import useMediaQuery from "@mui/material/useMediaQuery";
import useTheme from "@mui/material/styles/useTheme";
import { validateUnapplied, getPaymentRequest } from "./unappliedFunctions";
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Grid2 from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Backdrop from '@mui/material/Backdrop';
import CompletePayment from '../completePayment/CompletePayment';
import ManageWidget from '../completePayment/ManageWidget';
import PaymentDetails from '../completePayment/PaymentDetails';
import {useSxStyles as sx} from "../styles/sxStyles";
import UserReceipts from "../lookup/UserReceipts";
import AddIcCallTwoToneIcon from "@mui/icons-material/AddIcCallTwoTone";
import EnterResult from '../callLogging/EnterResult';
import PayorInfo from "./PayorInfo";
import PaymentInfo from "./PaymentInfo";
import PaymentsTwoToneIcon from "@mui/icons-material/PaymentsTwoTone";
import ReplayIcon from "@mui/icons-material/Replay";
import {getDecimal} from "../config/utils";

const UnappliedPayments = () => {
    const navigate = useNavigate();
    const [requestUrl,] = useRequestUrl();
    const [authToken,] = useAuthToken();
    const [courtData,] = useFullCourtData();
    const court = courtData ? courtData.court : null;
    const [courtsByAgency,] = useCourtsByAgency();
    const [agencyCourt, setAgencyCourt] = useAgencyCourt();
    const [user,] = useCurrentUser();
    const [logEntry, setLogEntry] = useLogEntry();
    const theme = useTheme();
    const smallScreen = useMediaQuery(theme.breakpoints.down('xl'));
    const [actionResponse, setActionResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [paymentRequest, setPaymentRequest] = useState(null);
    const [paymentResult, setPaymentResult] = useState(null);
    const [processingFee, setProcessingFee] = useState(0);
    const [openReceipt, setOpenReceipt] = useState(false);
    const [newCall, setNewCall] = useState(false);
    const [payorInfo, setPayorInfo] = useState({});
    const [paymentInfo, setPaymentInfo] = useState({dob: null, violationDate: null});
    const [amounts, setAmounts] = useState({base: 0, fee: 0, total: 0});
    const [clearValues, setClearValues] = useState(false)
    const [editAmt, setEditAmt] = useState(false);
    const [startPymt, setStartPymt] = useState(false);

    const clearMessages = () => {
        setErrorMessage(null);
        setActionResponse(null);
    }

    const getProcessingFee = (orgAmount) => {
        clearMessages();
        setLoading(true);
        setClearValues(false);
        const query = {
            courtId: courtData.court.courtId,
            paymentCategory: 'MOTO',
            paymentType: 'Card',
            courtAmount: orgAmount
        }
        const url = `${requestUrl}/calculateServiceFee`;
        API.postRetrieve(url, JSON.stringify(query), 'getProcessingFee', `Bearer ${authToken}`, setActionResponse);
    }

    const makePayment = () => {
        setStartPymt(false);
        clearMessages()
        const errors = validateUnapplied(amounts, payorInfo, paymentInfo);
        if (errors) {
            setErrorMessage(errors);
        } else {
            setPaymentRequest(getPaymentRequest(court.courtId, amounts, payorInfo, paymentInfo));
        }
    }

    useEffect(() => {
        if (loading && actionResponse) {
            if (!actionResponse.error) {
                if (actionResponse.requestType === 'getProcessingFee') {
                    setProcessingFee(actionResponse.citepayFee);
                }
            }
            setLoading(false);
        }
    }, [actionResponse])

    useEffect(() => {
        if (paymentInfo) {
            console.log('Refreshing screen');
        }
    }, [paymentInfo]);

    useEffect(() => {
        const feeAmt = processingFee ? processingFee : 0;
        setAmounts({...amounts, fee: feeAmt, total: parseFloat(getDecimal(amounts.base + feeAmt, false))});
        setEditAmt(false);
    }, [processingFee]);

    useEffect(() => {
        if (!editAmt && !loading && startPymt) makePayment();
    }, [editAmt, loading, startPymt]);

    useEffect(() => {
        if (paymentRequest) {
            setOpenDialog(true);
        }
    }, [paymentRequest])

    useEffect(() => {
        if (paymentResult) {
            setOpenReceipt(true);
        }
    }, [paymentResult])

    useEffect(() => {
        if (!openReceipt && paymentResult) {
            setPayorInfo({});
            setPaymentInfo({dob: null, violationDate: null});
            setProcessingFee(0);
            setAmounts({base: 0, fee: 0, total: 0});
            setClearValues(true);
            setPaymentRequest(null);
            setPaymentResult(null);
        }
    }, [openReceipt])

    useEffect(() => {
        if (!agencyCourt && courtsByAgency && courtsByAgency.courts && courtsByAgency.courts.length === 1) {
            setAgencyCourt(courtsByAgency.courts[0]);
        }
    }, [courtsByAgency, agencyCourt]);

    useEffect(() => {
        if (user && user.logCalls && !paymentResult && !logEntry) {
            navigate(-1);
        }
    }, [logEntry, paymentResult]);

    const feeResponse = {
        courtAmount: amounts.base,
        citepayFee: amounts.fee,
        totalAmount: amounts.total,
        courtId: court?.courtId
    };

    return (<>
        { paymentRequest && !paymentResult &&
            ( court?.neumoPayOnline
                ? <ManageWidget
                    openDialog={openDialog}
                    setOpenDialog={setOpenDialog}
                    paymentRequest={paymentRequest}
                    setPaymentRequest={setPaymentRequest}
                    setPaymentResult={setPaymentResult}
                    court={court}
                    feeResponse={feeResponse}
                />
                : <CompletePayment
                    openDialog={openDialog}
                    setOpenDialog={setOpenDialog}
                    paymentRequest={paymentRequest}
                    setPaymentResult={setPaymentResult}
                    court={court}
                />
            )
        }
        { paymentResult &&
            <PaymentDetails
                paymentRequest={paymentRequest}
                paymentResult={paymentResult}
                openReceipt={openReceipt}
                setOpenReceipt={setOpenReceipt}
            />
        }
        <Typography variant={smallScreen ?'h6' :'h5'} component='h1' color='primary' sx={sx.header}>Bond/Case Payment - {courtData.court.courtName}</Typography>
        { logEntry &&
            <Typography variant='h6' color='textSecondary'>
                Call from: {`${logEntry.callerFirstName} ${logEntry.callerLastName}`}
            </Typography>
        }
        <Grid2
            container
            spacing={3}
            justifyContent="center"
        >
            <Backdrop open={loading} sx={sx.backdrop}>
                <CircularProgress color='inherit' />
            </Backdrop>
            <Grid2
                sx={sx.gridBottom}
                size={{
                    xs: 12,
                    md: 4
                }}>
                { newCall ?
                    <EnterResult logEntry={logEntry} setLogEntry={setLogEntry} currentUser={user} setNewCall={setNewCall} /> :
                    <>
                        <PayorInfo payorInfo={payorInfo} setPayorInfo={setPayorInfo} clearValues={clearValues} />
                        <Paper elevation={3} sx={sx.controlPanel}>
                            <Typography variant='h6' component='h2' color='primary' sx={{marginBottom: '.5rem'}}>Payment Amounts</Typography>
                            <Grid2 container spacing={1} alignItems='top'>
                                <Grid2 sx={{textAlign: 'right'}} size={4}>
                                    <Typography component='span' color='primary'>{court && court.nonCourtOrganization ? 'Agency' : 'Court'}:</Typography>
                                </Grid2>
                                <Grid2 sx={{textAlign: 'right'}} size={4}><Typography component='span' color='primary'>Fee:</Typography></Grid2>
                                <Grid2 sx={{textAlign: 'right'}} size={4}><Typography component='span' color='primary'>Total:</Typography></Grid2>
                                <Grid2 sx={{textAlign: 'right'}} size={4}><Typography component='span' color='textPrimary'>{getDecimal(amounts.base)}</Typography></Grid2>
                                <Grid2 sx={{textAlign: 'right'}} size={4}><Typography component='span' color='textPrimary'>{getDecimal(amounts.fee)}</Typography></Grid2>
                                <Grid2 sx={{textAlign: 'right'}} size={4}><Typography component='span' color='textPrimary'>{getDecimal(amounts.total)}</Typography></Grid2>
                            </Grid2>
                        </Paper>

                        <Box component='div' flexWrap='wrap-reverse' sx={{display: 'flex', flexDirection: 'row-reverse', margin: '1rem', marginBottom: '0'}}>
                            <Button
                                variant='contained'
                                color='primary'
                                startIcon={<PaymentsTwoToneIcon/>}
                                disabled={loading}
                                sx={sx.squashedButton}
                                onMouseDown={() => setStartPymt(true)}
                                onKeyUp={(e) => {if (e.key === 'Enter') setStartPymt(true)}}
                            >
                                Complete Payment
                            </Button>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<ReplayIcon/>}
                                sx={sx.squashedButton}
                                onClick={() => navigate(-1)}
                            >
                                Back
                            </Button>
                            { user && user.printUserReceipts &&
                                <UserReceipts />
                            }
                            { logEntry &&
                                <Button
                                    variant='contained'
                                    color='primary'
                                    startIcon={<AddIcCallTwoToneIcon />}
                                    sx={sx.squashedButton}
                                    onClick={() => setNewCall(true)}
                                >
                                    Log Call
                                </Button>
                            }
                        </Box>
                    </>
                }
            </Grid2>
            <Grid2
                sx={sx.gridBottom}
                size={{
                    xs: 12,
                    md: 8
                }}>
                { errorMessage &&
                    <Alert severity='error' sx={{marginTop: '1rem', textAlign: 'left'}}>
                        <AlertTitle>Error!</AlertTitle>
                        {errorMessage}
                    </Alert>
                }
                <PaymentInfo
                    paymentInfo={paymentInfo}
                    amounts={amounts}
                    getProcessingFee={getProcessingFee}
                    clearValues={clearValues}
                    setEditAmt={setEditAmt}
                />
            </Grid2>
        </Grid2>
    </>);
}

export default UnappliedPayments;
